import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminSeason, SeasonStatus } from '@/types/admin';

const STATUS_LABELS: Record<SeasonStatus, string> = {
  ACTIVE: 'Ativa',
  UPCOMING: 'Próxima',
  COMPLETED: 'Finalizada',
};

const STATUS_COLORS: Record<SeasonStatus, string> = {
  ACTIVE: 'bg-green-500/20 text-green-400',
  UPCOMING: 'bg-blue-500/20 text-blue-400',
  COMPLETED: 'bg-gray-500/20 text-gray-400',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const AdminSeasonsPage: FC = () => {
  const { showToast } = useToast();
  const [seasons, setSeasons] = useState<AdminSeason[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSeasons();
  }, []);

  const loadSeasons = async () => {
    try {
      const data = await adminService.getSeasons();
      setSeasons(data);
    } catch { /* ignore */ } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir esta temporada?')) return;
    try {
      await adminService.deleteSeason(id);
      await loadSeasons();
    } catch {
      showToast('Erro ao excluir temporada', 'error');
    }
  };

  const handleFinalize = async (id: string) => {
    if (!confirm('Finalizar esta temporada? Isso criará um snapshot do ranking atual e encerrará a temporada.')) return;
    try {
      const result = await adminService.finalizeSeason(id);
      showToast(`Temporada finalizada! ${result.snapshot_count} posições salvas no snapshot.`, 'success');
      await loadSeasons();
    } catch {
      showToast('Erro ao finalizar temporada', 'error');
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold">Temporadas</h1>
        <Link
          to="/admin/temporadas/nova"
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-xl">add</span>
          Nova Temporada
        </Link>
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : seasons.length === 0 ? (
        <p className="text-content-muted">Nenhuma temporada criada.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <th className="pb-3 font-medium">Temporada</th>
                <th className="pb-3 font-medium text-center">Status</th>
                <th className="pb-3 font-medium">Período</th>
                <th className="pb-3 font-medium text-center">Dias Restantes</th>
                <th className="pb-3 font-medium text-center">Snapshots</th>
                <th className="pb-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {seasons.map((season) => (
                <tr key={season.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-2xl text-accent">emoji_events</span>
                      <div>
                        <p className="font-medium">{season.name}</p>
                        {season.phase && (
                          <p className="text-content-muted text-sm">{season.phase}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[season.status]}`}>
                      {STATUS_LABELS[season.status]}
                    </span>
                  </td>
                  <td className="py-4 text-sm text-content-secondary">
                    {formatDate(season.start_date)} — {formatDate(season.end_date)}
                  </td>
                  <td className="py-4 text-center text-sm">
                    {season.status === 'ACTIVE' ? (
                      <span className="text-green-400 font-medium">{season.days_remaining}d</span>
                    ) : (
                      <span className="text-content-muted">—</span>
                    )}
                  </td>
                  <td className="py-4 text-center text-sm">
                    {season.snapshot_count > 0 ? season.snapshot_count : '—'}
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {(season.status === 'ACTIVE' || season.status === 'COMPLETED') && (
                        <Link
                          to={`/admin/temporadas/${season.id}/ranking`}
                          className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                          title="Ver Ranking"
                        >
                          <span className="material-symbols-outlined text-xl">leaderboard</span>
                        </Link>
                      )}
                      <Link
                        to={`/admin/temporadas/${season.id}/editar`}
                        className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                        title="Editar"
                      >
                        <span className="material-symbols-outlined text-xl">edit</span>
                      </Link>
                      {season.status !== 'COMPLETED' && (
                        <button
                          onClick={() => handleFinalize(season.id)}
                          className="p-2 rounded-lg hover:bg-amber-500/10 text-content-muted hover:text-amber-400 transition-colors"
                          title="Finalizar"
                        >
                          <span className="material-symbols-outlined text-xl">flag</span>
                        </button>
                      )}
                      {season.status === 'UPCOMING' && (
                        <button
                          onClick={() => handleDelete(season.id)}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-content-muted hover:text-red-400 transition-colors"
                          title="Excluir"
                        >
                          <span className="material-symbols-outlined text-xl">delete</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSeasonsPage;
